/*
 * cart-share.js
 * "Share cart with store" feature (Route A — no server).
 *
 * What it does:
 *  - Reads the customer's current cart.
 *  - Encrypts it with a PUBLIC lock (the key below). This key can ONLY encrypt,
 *    it can NEVER decrypt — so it is safe to expose here in the published theme.
 *  - Shows the customer a code they can copy and send to the store.
 *
 * Only the store owner's private decoder (tools/cart-decoder.html) holds the
 * matching secret key, so only the owner can open the cart. If you ever rotate
 * the keys, replace PUBLIC_KEY_B64 below with the new public key AND update the
 * private key inside tools/cart-decoder.html to match.
 */
(function () {
  "use strict";

  // ---- Public lock (RSA-OAEP, 2048-bit, SPKI/DER, base64). Safe to publish. ----
  var PUBLIC_KEY_B64 =
    "MIIBIjANBgkqhkiG9w0BAQEFAAOCAQ8AMIIBCgKCAQEAkC1k/qmsiakfELM54LRFDF9DzSMgF99KEDxaD1Rp25zlQ9figxlmMguDiOJnBCUQNz9OOjddTCYDoxcZhoXW7jD2Oet0ENP2zOz3cNUr7Ix615QLGf4o3+oNk7Vyo1WKIuxcv/7qWURuG7eM+wGi1f8PXi5zcIF3cjcDXnI6ypg+axZuUicoJkd1hXGQjttO3knFmEfbPvqvjlL4U6WwocincqT7oT3r86PJQreXR5z8J8WrfKYQRQkIoSsiIKoMZvOWHf2ZCJ0hJvgVvlzuU96F3sb5+FeIarbIwKzldGzgF9ExqkrekQjN5z5ex7pxR6fhJGpqmEKKG7BxOMFGnwIDAQAB";

  // ---------------------------------------------------------------------------
  // Small helpers
  // ---------------------------------------------------------------------------
  function b64ToBytes(b64) {
    var bin = atob(b64);
    var bytes = new Uint8Array(bin.length);
    for (var i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
    return bytes;
  }

  function bytesToB64url(bytes) {
    var bin = "";
    for (var i = 0; i < bytes.length; i++) bin += String.fromCharCode(bytes[i]);
    return btoa(bin).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
  }

  var publicKeyPromise = null;
  function getPublicKey() {
    if (!publicKeyPromise) {
      publicKeyPromise = crypto.subtle.importKey(
        "spki",
        b64ToBytes(PUBLIC_KEY_B64),
        { name: "RSA-OAEP", hash: "SHA-256" },
        false,
        ["encrypt"]
      );
    }
    return publicKeyPromise;
  }

  // Compress bytes with the browser's built-in deflate (raw). Big carts repeat
  // the same CDN image/URL prefixes on every line, so this shrinks the code a lot
  // (often 85–93% smaller). Returns a Promise<Uint8Array>.
  function deflate(bytes) {
    var cs = new CompressionStream("deflate-raw");
    var writer = cs.writable.getWriter();
    writer.write(bytes);
    writer.close();
    return new Response(cs.readable).arrayBuffer().then(function (buf) {
      return new Uint8Array(buf);
    });
  }

  // ---------------------------------------------------------------------------
  // Encryption: AES-GCM for the cart data + RSA-OAEP to wrap the AES key.
  // Code layout (then base64url-encoded):
  //   [1 byte version][256 bytes RSA-wrapped AES key][12 bytes IV][ciphertext]
  // version 1 = ciphertext is plain JSON, version 2 = ciphertext is zipped JSON.
  // We zip when the browser supports it (all modern ones); otherwise we fall back
  // to version 1 so older browsers still work. The decoder reads both.
  // ---------------------------------------------------------------------------
  function encryptCart(cartObj) {
    var jsonBytes = new TextEncoder().encode(JSON.stringify(cartObj));
    var version = 1;
    var payloadPromise;
    if (typeof CompressionStream === "function") {
      version = 2;
      payloadPromise = deflate(jsonBytes).catch(function () {
        version = 1; // zipping failed — send it uncompressed instead of breaking
        return jsonBytes;
      });
    } else {
      payloadPromise = Promise.resolve(jsonBytes);
    }

    var aesKey, iv, ciphertext;
    return payloadPromise
      .then(function (payload) {
        return crypto.subtle
          .generateKey({ name: "AES-GCM", length: 256 }, true, ["encrypt"])
          .then(function (k) {
            aesKey = k;
            iv = crypto.getRandomValues(new Uint8Array(12));
            return crypto.subtle.encrypt({ name: "AES-GCM", iv: iv }, aesKey, payload);
          });
      })
      .then(function (ct) {
        ciphertext = new Uint8Array(ct);
        return crypto.subtle.exportKey("raw", aesKey);
      })
      .then(function (rawAes) {
        return getPublicKey().then(function (pub) {
          return crypto.subtle.encrypt({ name: "RSA-OAEP" }, pub, rawAes);
        });
      })
      .then(function (wrapped) {
        var encKey = new Uint8Array(wrapped); // 256 bytes for RSA-2048
        var blob = new Uint8Array(1 + encKey.length + iv.length + ciphertext.length);
        blob[0] = version;
        blob.set(encKey, 1);
        blob.set(iv, 1 + encKey.length);
        blob.set(ciphertext, 1 + encKey.length + iv.length);
        return bytesToB64url(blob);
      });
  }

  // ---------------------------------------------------------------------------
  // Read the EXACT prices the customer sees on the rendered cart page, keyed by
  // line-item key. This captures the theme's quantity-tier + sale prices, which
  // /cart.js does not include. Best-effort: falls back to /cart.js numbers.
  // ---------------------------------------------------------------------------
  function txt(node) {
    return node ? node.textContent.trim() : null;
  }

  function readDisplayByKey() {
    var map = {};
    document.querySelectorAll("cart-line-item").forEach(function (el) {
      var key = el.dataset.key;
      if (!key) return;
      var unitWrap = el.querySelector(".cart-table_column-2.line-item_price") || el.querySelector(".line-item_price");
      var totalWrap =
        el.querySelector(".cart-table_page__column-4.line-item_total-price") ||
        el.querySelector(".line-item_total-price");
      map[key] = {
        pu: unitWrap && (txt(unitWrap.querySelector(".price-sale")) || txt(unitWrap.querySelector(".price"))),
        pc: unitWrap && txt(unitWrap.querySelector(".price-compare")),
        lt: totalWrap && (txt(totalWrap.querySelector(".price-sale")) || txt(totalWrap.querySelector(".price"))),
        lc: totalWrap && txt(totalWrap.querySelector(".price-compare"))
      };
    });
    return map;
  }

  function readCartTotals() {
    function vis(node) {
      return node && !node.classList.contains("d-none") ? node.textContent.trim() : null;
    }
    return {
      subtotal: txt(document.querySelector(".js-cart-total-price")),
      original: vis(document.querySelector(".js-cart-original-total")),
      savings: vis(document.querySelector(".js-cart-saved"))
    };
  }

  function thumb(url) {
    if (!url) return "";
    return url + (url.indexOf("?") >= 0 ? "&" : "?") + "width=160";
  }

  // ---------------------------------------------------------------------------
  // Build a complete, readable snapshot of the current cart (all fields).
  // ---------------------------------------------------------------------------
  function buildCartObject() {
    return fetch("/cart.js", { headers: { Accept: "application/json" } })
      .then(function (r) {
        return r.json();
      })
      .then(function (cart) {
        var display = {};
        var totals = { subtotal: null, original: null, savings: null };
        try {
          display = readDisplayByKey();
          totals = readCartTotals();
        } catch (e) {}

        var items = (cart.items || []).map(function (it) {
          var name = it.product_title || it.title || "";
          if (it.variant_title && it.variant_title !== "Default Title") {
            name += " — " + it.variant_title;
          }
          var opts = (it.options_with_values || [])
            .filter(function (o) {
              return o && o.value && o.value !== "Default Title" && o.name !== "Title";
            })
            .map(function (o) {
              return o.name + ": " + o.value;
            });
          var props = [];
          if (it.properties) {
            Object.keys(it.properties).forEach(function (k) {
              if (k.charAt(0) === "_") return; // hidden props
              var v = it.properties[k];
              if (v === null || v === "") return;
              props.push(k + ": " + String(v));
            });
          }
          var d = display[it.key] || {};
          return {
            id: it.variant_id,
            q: it.quantity,
            t: name,
            sku: it.sku || "",
            url: it.url || "",
            img: thumb(it.image || (it.featured_image && it.featured_image.url)),
            opts: opts,
            props: props,
            p: it.price, // unit, cents (fallback)
            line: it.final_line_price != null ? it.final_line_price : it.line_price, // cents (fallback)
            pu: d.pu || null, // displayed unit price (tier/sale)
            pc: d.pc || null, // displayed compare-at unit
            lt: d.lt || null, // displayed line total
            lc: d.lc || null // displayed compare line total
          };
        });

        // Cart-level discounts / discount codes (whatever is applied to the cart).
        var discounts = (cart.cart_level_discount_applications || []).map(function (d) {
          return {
            title: d.title || d.code || "",
            code: d.type === "discount_code" ? d.title || d.code || "" : "",
            amount: d.total_allocated_amount != null ? d.total_allocated_amount : null
          };
        });
        var codes = discounts
          .filter(function (d) {
            return d.code;
          })
          .map(function (d) {
            return d.code;
          });

        // Custom cart attributes (e.g. gift message, delivery date).
        var attributes = [];
        if (cart.attributes) {
          Object.keys(cart.attributes).forEach(function (k) {
            var v = cart.attributes[k];
            if (v === "" || v == null) return;
            attributes.push(k + ": " + String(v));
          });
        }

        return {
          v: 2,
          ts: Date.now(),
          currency: cart.currency || "",
          note: cart.note || "",
          count: cart.item_count || 0,
          total: cart.total_price || 0,
          totals: totals,
          requiresShipping: cart.requires_shipping !== false,
          totalDiscount: cart.total_discount || 0,
          discounts: discounts,
          codes: codes,
          attributes: attributes,
          items: items
        };
      });
  }

  // ---------------------------------------------------------------------------
  // UI wiring
  // ---------------------------------------------------------------------------
  function qs(sel, root) {
    return (root || document).querySelector(sel);
  }

  function openModal(modal) {
    modal.removeAttribute("hidden");
    document.documentElement.style.overflow = "hidden";
  }
  function closeModal(modal) {
    modal.setAttribute("hidden", "");
    document.documentElement.style.overflow = "";
  }

  function init() {
    var btn = qs(".js-share-cart-btn");
    var modal = qs(".js-share-cart-modal");
    if (!btn || !modal) return;

    var whatsappBtn = qs(".js-share-cart-whatsapp", modal);
    var emailBtn = qs(".js-share-cart-email", modal);
    var statusEl = qs(".js-share-cart-status", modal);
    var spinnerLabel = btn.getAttribute("data-loading-label") || "Preparing…";
    var defaultLabel = btn.innerHTML;

    if (!window.crypto || !crypto.subtle) {
      // Very old browser — hide the feature rather than show a broken button.
      btn.style.display = "none";
      return;
    }

    // The code travels inside the WhatsApp/mailto link, which clients cap in length.
    // Most mail clients accept a few KB; wa.me is more generous. We set both links for
    // any size and only warn for very large carts.
    var MAILTO_SAFE = 6000;
    var WHATSAPP_SAFE = 12000;

    btn.addEventListener("click", function () {
      btn.disabled = true;
      btn.textContent = spinnerLabel;
      buildCartObject()
        .then(function (cartObj) {
          if (!cartObj.items.length) {
            throw new Error(btn.getAttribute("data-empty-label") || "Your cart is empty.");
          }
          return encryptCart(cartObj);
        })
        .then(function (code) {
          // WhatsApp: open a chat to the store number with the code pre-filled.
          if (whatsappBtn) {
            var waIntro = whatsappBtn.getAttribute("data-intro") || "Here is my cart code:";
            var waPhone = (whatsappBtn.getAttribute("data-phone") || "").replace(/[^0-9]/g, "");
            whatsappBtn.href =
              "https://wa.me/" + waPhone + "?text=" + encodeURIComponent(waIntro + "\n\n" + code);
          }
          // Email: open a message to support with the code pre-filled in the body.
          if (emailBtn) {
            var mailIntro = emailBtn.getAttribute("data-intro") || "Here is my cart code:";
            var subject = encodeURIComponent(emailBtn.getAttribute("data-subject") || "Help with my order — my cart code");
            var body = encodeURIComponent(mailIntro + "\n\n" + code);
            emailBtn.href =
              "mailto:" + (emailBtn.getAttribute("data-to") || "") + "?subject=" + subject + "&body=" + body;
          }

          if (statusEl) {
            statusEl.textContent =
              code.length > WHATSAPP_SAFE
                ? statusEl.getAttribute("data-warn-large") ||
                  "This is a large cart — if the full details don't come through, just send your message and we'll pull up your cart."
                : code.length > MAILTO_SAFE
                ? statusEl.getAttribute("data-warn-mailto") ||
                  "Large cart — WhatsApp is the most reliable way to send it."
                : "";
          }

          openModal(modal);
        })
        .catch(function (err) {
          alert((err && err.message) || btn.getAttribute("data-error") || "Sorry, we couldn't prepare your cart.");
        })
        .finally(function () {
          btn.disabled = false;
          btn.innerHTML = defaultLabel;
        });
    });

    modal.addEventListener("click", function (e) {
      if (e.target === modal || e.target.classList.contains("js-share-cart-close")) {
        closeModal(modal);
      }
    });
    // After a share button is tapped, its link opens WhatsApp/email — close the popup.
    function closeSoon() { setTimeout(function () { closeModal(modal); }, 300); }
    if (whatsappBtn) whatsappBtn.addEventListener("click", closeSoon);
    if (emailBtn) emailBtn.addEventListener("click", closeSoon);
    document.addEventListener("keydown", function (e) {
      if (e.key === "Escape" && !modal.hasAttribute("hidden")) closeModal(modal);
    });
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init);
  } else {
    init();
  }
})();
